import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ContatoDto, EmpresaDto, EmpresaEntrada, EmpresaResumoDto, Pagina } from '@crm/shared'
import { api } from './cliente'

/**
 * Empresas, e o vínculo delas com os contatos.
 *
 * Mexer numa empresa muda o que aparece em três lugares: a lista de empresas,
 * a de contatos (coluna Empresa e o dono, quando a empresa é transferida) e o
 * quadro de negociações (o dono do card). Por isso toda mudança invalida as
 * três árvores de uma vez; esquecer uma deixaria a tela dizendo um dono e o
 * banco outro até alguém recarregar.
 */

export interface FiltroEmpresasTela {
  pagina: number
  tamanho: number
  busca?: string
  vendedorId?: string
  ordenar?: 'nome' | 'recentes'
}

export interface OpcaoDeEmpresa {
  id: string
  nome: string
  cnpj: string | null
  vendedorNome: string | null
}

export const chavesEmpresas = {
  raiz: ['empresas'] as const,
  lista: (filtro: FiltroEmpresasTela) => ['empresas', 'lista', filtro] as const,
  detalhe: (id: string) => ['empresas', 'detalhe', id] as const,
  opcoes: (busca: string) => ['empresas', 'opcoes', busca] as const,
}

function invalidarTudo(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: chavesEmpresas.raiz })
  void cliente.invalidateQueries({ queryKey: ['contatos'] })
  void cliente.invalidateQueries({ queryKey: ['negociacoes'] })
}

export function useEmpresas(filtro: FiltroEmpresasTela) {
  return useQuery({
    queryKey: chavesEmpresas.lista(filtro),
    queryFn: () =>
      api<Pagina<EmpresaResumoDto>>('/api/empresas', {
        query: {
          pagina: filtro.pagina,
          tamanho: filtro.tamanho,
          busca: filtro.busca,
          vendedorId: filtro.vendedorId,
          ordenar: filtro.ordenar,
        },
      }),
    // Digitar na busca não pisca a tabela vazia entre uma letra e outra.
    placeholderData: keepPreviousData,
  })
}

export function useEmpresa(id: string | null) {
  return useQuery({
    queryKey: chavesEmpresas.detalhe(id ?? ''),
    queryFn: () => api<EmpresaDto>(`/api/empresas/${id}`),
    enabled: Boolean(id),
  })
}

export function useOpcoesDeEmpresa(busca: string, habilitado: boolean) {
  return useQuery({
    queryKey: chavesEmpresas.opcoes(busca),
    queryFn: () => api<OpcaoDeEmpresa[]>('/api/empresas/opcoes', { query: { busca } }),
    enabled: habilitado,
    placeholderData: keepPreviousData,
  })
}

export function useCriarEmpresa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: EmpresaEntrada) =>
      api<EmpresaDto>('/api/empresas', { metodo: 'POST', corpo: dados }),
    onSuccess: () => invalidarTudo(cliente),
  })
}

export function useAtualizarEmpresa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<EmpresaEntrada> }) =>
      api<EmpresaDto>(`/api/empresas/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => invalidarTudo(cliente),
  })
}

export function useExcluirEmpresa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/empresas/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => invalidarTudo(cliente),
  })
}

/** Liga o contato a uma empresa, ou solta (`empresaId: null`). */
export function useVincularEmpresa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ contatoId, empresaId }: { contatoId: string; empresaId: string | null }) =>
      api<ContatoDto>(`/api/contatos/${contatoId}/empresa`, {
        metodo: 'PUT',
        corpo: { empresaId },
      }),
    onSuccess: () => {
      invalidarTudo(cliente)
      // A ficha do bate-papo e o cartão da conversa mostram o dono.
      void cliente.invalidateQueries({ queryKey: ['conversas'] })
    },
  })
}
